import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { message } from 'antd';
import { tenderAPI, analyticsAPI } from '../services/api';

/**
 * 完整意向書頁面 - 對應 Google Form 所有欄位
 * 包含經銷商資料填寫 + 品牌選擇
 * + Comboware AI Infra 產品簡報展示 (addon-2)
 */

const TAIWAN_REGIONS = [
  '基隆市', '新北市', '台北市', '桃園市', '新竹縣(市)', '苗栗縣',
  '台中市', '彰化縣', '雲林縣', '南投縣', '嘉義縣(市)', '台南市',
  '高雄市', '屏東縣', '宜蘭縣', '花蓮縣', '台東縣', '澎湖縣',
  '金門縣', '連江縣',
];

// ---------------------------------------------------------------------------
// Comboware AI Infra Slide Data (from ComboLLM_Provider_Platform_Sales_Deck_v6)
// ---------------------------------------------------------------------------
const SLIDES = [
  {
    title: 'WHY COMBOWARE?',
    subtitle: '企業 AI 基礎設施的挑戰與 Comboware 的差異化優勢',
    content: (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff1f0', borderRadius: 10, padding: 16, border: '1px solid #ffa39e' }}>
            <div style={{ fontWeight: 700, color: '#cf1322', marginBottom: 8, fontSize: 15 }}>現有方案的局限</div>
            {['VMware / Nutanix 不管理設備與機房','GPU Manager 平台缺乏全面管理','三大公有雲成本高，缺乏資源靈活性','AI/雲端/企業應用各自獨立管理','缺乏全球資源共享機制'].map((t,i)=>(
              <div key={i} style={{ fontSize: 13, color: '#595959', padding: '3px 0' }}>✗ {t}</div>
            ))}
          </div>
          <div style={{ background: '#f6ffed', borderRadius: 10, padding: 16, border: '1px solid #b7eb8f' }}>
            <div style={{ fontWeight: 700, color: '#389e0d', marginBottom: 8, fontSize: 15 }}>Comboware AI 平台優勢</div>
            {['DCIM 整合設備+機房管理，AI 自動化必備','一站式統一管理 20+ 種服務類型','全球資源共享，CP 值遠超公有雲','Ansible + Docker 全自動零接觸部署','全球軟體商一鍵上架，硬體+軟體同平台'].map((t,i)=>(
              <div key={i} style={{ fontSize: 13, color: '#595959', padding: '3px 0' }}>✓ {t}</div>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {[{n:'70%',l:'部署時間節省'},{n:'50%+',l:'成本優勢 vs 公有雲'},{n:'3x',l:'服務上架加速'},{n:'100%',l:'設備可視化(DCIM)'}].map((s,i)=>(
            <div key={i} style={{ textAlign: 'center', background: '#e6f7ff', borderRadius: 8, padding: '12px 6px' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0050b3' }}>{s.n}</div>
              <div style={{ fontSize: 11, color: '#595959', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: 'GLOBAL ECOSYSTEM',
    subtitle: '10 種角色 — 打造全方位硬體+軟體+服務生態圈',
    content: (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            {icon:'💻',name:'Software Provider',desc:'全球軟體商上架'},
            {icon:'🧠',name:'AI Model Provider',desc:'LLM/RAG 模型上架'},
            {icon:'📱',name:'AP Provider',desc:'企業應用快速交付'},
            {icon:'🖥️',name:'Hardware Provider',desc:'全球伺服器共享'},
            {icon:'⚡',name:'GPU Provider',desc:'高效能算力供應'},
            {icon:'🔧',name:'Non-GPU Provider',desc:'CPU/記憶體/儲存'},
            {icon:'🎓',name:'Professional Advisor',desc:'技術架構諮詢'},
            {icon:'👨‍💻',name:'Developer',desc:'API 整合與客製'},
            {icon:'📋',name:'Consultant',desc:'數位轉型規劃'},
            {icon:'🏫',name:'Coach',desc:'技術教育訓練'},
          ].map((r,i)=>(
            <div key={i} style={{ textAlign:'center', background:'#f0f5ff', borderRadius:8, padding:'10px 4px', border:'1px solid #d6e4ff' }}>
              <div style={{ fontSize: 22 }}>{r.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#003a8c', marginTop: 4 }}>{r.name}</div>
              <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>{r.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ background: '#f0f5ff', borderRadius: 8, padding: 12, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#003a8c' }}>
          完整產業生態圈 = 硬體商 + 軟體商 + AI 模型商 + 顧問 + 開發者 + 教練
        </div>
      </div>
    ),
  },
  {
    title: 'GLOBAL RESOURCE SHARING',
    subtitle: '全球資源共享 — CP 值完勝三大公有雲',
    content: (
      <div>
        <div style={{ marginBottom: 16 }}>
          {[
            {name:'AWS / Azure / GCP', cost:'$$$$', note:'高成本、廠商鎖定', color:'#ff4d4f'},
            {name:'VMware / Nutanix', cost:'$$$', note:'授權費高、不管設備', color:'#fa8c16'},
            {name:'GPU 專用平台', cost:'$$', note:'功能單一、不含管理', color:'#faad14'},
            {name:'Comboware', cost:'$', note:'全功能、全球共享、最高 CP 值', color:'#52c41a'},
          ].map((c,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px', borderRadius:6, marginBottom:6, background: i===3?'#f6ffed':'#fafafa', border: i===3?'2px solid #52c41a':'1px solid #f0f0f0' }}>
              <div style={{ fontWeight:700, minWidth:140, fontSize:13 }}>{c.name}</div>
              <div style={{ fontWeight:800, color:c.color, minWidth:50, fontSize:15 }}>{c.cost}</div>
              <div style={{ fontSize:12, color:'#8c8c8c' }}>{c.note}</div>
            </div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div style={{ background:'#e6f7ff', borderRadius:8, padding:12 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#003a8c', marginBottom:6 }}>👨‍💻 軟體商視角</div>
            {['Docker Compose 打包上傳即上架','自動生成部署表單','快速觸達全球客戶'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>▸ {t}</div>
            ))}
          </div>
          <div style={{ background:'#fff7e6', borderRadius:8, padding:12 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#ad6800', marginBottom:6 }}>🏢 客戶視角</div>
            {['同一平台同時獲得硬體+軟體服務','一站式採購，無需多方比價','全球資源按需取用'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>▸ {t}</div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'AI MODEL STRATEGY',
    subtitle: 'Public vs Private Model — 依需求智選最佳 AI 模型',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
          <div style={{ background:'#f6ffed', borderRadius:10, padding:14, border:'1px solid #b7eb8f' }}>
            <div style={{ fontWeight:700, fontSize:14, color:'#389e0d', marginBottom:8 }}>🔒 Private Model</div>
            {['私有部署成本低，適合日常 AI 任務','資料不離開企業，符合合規需求','會計/財務系統敏感數據','企業知識庫 RAG + Private Model'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>▸ {t}</div>
            ))}
          </div>
          <div style={{ background:'#e6f7ff', borderRadius:10, padding:14, border:'1px solid #91d5ff' }}>
            <div style={{ fontWeight:700, fontSize:14, color:'#0050b3', marginBottom:8 }}>🌐 Public Model</div>
            {['GPT / Gemini / Claude 等頂級模型','需要強大推理能力時選用','程式開發、策略分析、創意內容','無需部署，API 直接調用'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>▸ {t}</div>
            ))}
          </div>
        </div>
        <div style={{ background:'#fafafa', borderRadius:8, padding:12 }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:8, color:'#262626' }}>智選決策矩陣</div>
          <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f0f5ff' }}>
              {['使用場景','推薦模型','成本','安全性','AI 能力'].map((h,i)=>(
                <th key={i} style={{ padding:'6px 8px', textAlign:'left', fontWeight:600, borderBottom:'2px solid #d6e4ff' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[
                ['行政助理 / 日常 AI','Private','$ 低','★★★★★','★★★'],
                ['會計 / 財務系統','Private','$ 低','★★★★★','★★★'],
                ['程式開發 / 策略分析','Public','$$$ 高','★★★','★★★★★'],
                ['研究 / 創意內容','Public','$$$ 高','★★★','★★★★★'],
              ].map((r,i)=>(
                <tr key={i} style={{ borderBottom:'1px solid #f0f0f0' }}>
                  {r.map((c,j)=>(<td key={j} style={{ padding:'5px 8px' }}>{c}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    title: 'FLEXIBLE RESOURCE UTILIZATION',
    subtitle: '資源彈性運用 — 變成本為營收，化需求為供給',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
          {[
            {icon:'💰', title:'多餘資源 → 額外營收', items:['將閒置運算資源註冊到平台','自動匹配全球需求方','資源利用率 30% → 80%+'], bg:'#f6ffed', border:'#b7eb8f'},
            {icon:'📉', title:'低成本資源取得', items:['從全球平台取得低成本運算','比公有雲節省 50%+ 費用','即選即用，無需長期合約'], bg:'#e6f7ff', border:'#91d5ff'},
            {icon:'⚡', title:'臨時需求即時滿足', items:['短期需求無需採購設備','從外部資源池即時取得算力','專案結束即釋放，零浪費'], bg:'#fff7e6', border:'#ffd591'},
          ].map((c,i)=>(
            <div key={i} style={{ background:c.bg, borderRadius:10, padding:12, border:`1px solid ${c.border}` }}>
              <div style={{ fontSize:24, marginBottom:6 }}>{c.icon}</div>
              <div style={{ fontWeight:700, fontSize:12, color:'#262626', marginBottom:6 }}>{c.title}</div>
              {c.items.map((t,j)=>(<div key={j} style={{ fontSize:11, color:'#595959', padding:'2px 0' }}>▸ {t}</div>))}
            </div>
          ))}
        </div>
        <div style={{ background:'linear-gradient(90deg, #f0f5ff, #e6fffb)', borderRadius:8, padding:12, textAlign:'center' }}>
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, flexWrap:'wrap', fontSize:12, fontWeight:600, color:'#003a8c' }}>
            <span>企業閒置資源</span><span>→</span><span>註冊到 Comboware</span><span>→</span><span>全球需求方取用</span><span>→</span><span>產生營收回饋</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'NOT JUST A PLATFORM',
    subtitle: '30 年企業 IT 服務經驗的全方位夥伴',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:8, marginBottom:16 }}>
          {[
            {icon:'🏆', title:'30 年 IT Infra', desc:'深耕企業委外服務'},
            {icon:'🌏', title:'美國·澳洲·亞洲', desc:'全球 IDC 運算支援'},
            {icon:'🔧', title:'解決資源不足', desc:'即時補充運算資源'},
            {icon:'🛡️', title:'異地備份備援', desc:'災難恢復零中斷'},
            {icon:'🤝', title:'完整資訊服務', desc:'端到端 IT 服務'},
          ].map((c,i)=>(
            <div key={i} style={{ textAlign:'center', background:'#f0f5ff', borderRadius:8, padding:'12px 6px', border:'1px solid #d6e4ff' }}>
              <div style={{ fontSize:24 }}>{c.icon}</div>
              <div style={{ fontSize:11, fontWeight:700, color:'#003a8c', marginTop:4 }}>{c.title}</div>
              <div style={{ fontSize:10, color:'#8c8c8c', marginTop:2 }}>{c.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div style={{ background:'#fff1f0', borderRadius:8, padding:12, border:'1px solid #ffa39e' }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#cf1322', marginBottom:4 }}>❌ 其他平台商</div>
            <div style={{ fontSize:12, color:'#595959' }}>僅提供管理平台軟體，無資訊服務、無 IDC 支援、無委外服務</div>
          </div>
          <div style={{ background:'#f6ffed', borderRadius:8, padding:12, border:'1px solid #b7eb8f' }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#389e0d', marginBottom:4 }}>✅ Comboware AI Infra</div>
            <div style={{ fontSize:12, color:'#595959' }}>管理平台 + 30年服務 + 全球 IDC + 設備整合，真正一站式夥伴</div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'GLOBAL IDC NETWORK',
    subtitle: '全球 IDC 佈局 — 突破台灣 AI 電力瓶頸',
    content: (
      <div>
        <div style={{ background:'#fff7e6', borderRadius:8, padding:12, marginBottom:16, border:'1px solid #ffd591' }}>
          <div style={{ fontWeight:700, fontSize:13, color:'#ad6800', marginBottom:4 }}>⚡ 台灣 AI 發展面臨嚴峻的電力供應瓶頸</div>
          <div style={{ fontSize:12, color:'#8c6e00' }}>AI 訓練需要龐大電力 · 台灣電力供應日趨緊張 · GPU 機房散熱成本高昂 · 單一地區部署風險集中</div>
        </div>
        <div style={{ display:'flex', justifyContent:'center', gap:10, flexWrap:'wrap', marginBottom:16 }}>
          {[
            {flag:'🇻🇳',name:'越南'},{flag:'🇹🇭',name:'泰國'},{flag:'🇲🇾',name:'馬來西亞'},
            {flag:'🇯🇵',name:'日本'},{flag:'🇦🇺',name:'澳洲'},{flag:'🇺🇸',name:'美國'},
          ].map((c,i)=>(
            <div key={i} style={{ textAlign:'center', background:'#f0f5ff', borderRadius:8, padding:'10px 16px', border:'1px solid #d6e4ff' }}>
              <div style={{ fontSize:28 }}>{c.flag}</div>
              <div style={{ fontSize:12, fontWeight:600, color:'#003a8c', marginTop:2 }}>{c.name}</div>
            </div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
          {[
            {icon:'⚡', title:'分散電力風險', desc:'AI 算力分散至全球各地 IDC'},
            {icon:'🔒', title:'安全 VPN 互聯', desc:'資料傳輸加密無死角'},
            {icon:'🌐', title:'就近服務低延遲', desc:'覆蓋亞太、北美、大洋洲'},
          ].map((c,i)=>(
            <div key={i} style={{ textAlign:'center', background:'#e6fffb', borderRadius:8, padding:10, border:'1px solid #87e8de' }}>
              <div style={{ fontSize:20 }}>{c.icon}</div>
              <div style={{ fontSize:12, fontWeight:700, color:'#006d75', marginTop:4 }}>{c.title}</div>
              <div style={{ fontSize:11, color:'#8c8c8c', marginTop:2 }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: 'PLATFORM OVERVIEW',
    subtitle: '20+ 服務類型，一個平台全搞定',
    content: (
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
        {[
          {icon:'🤖', title:'AI 服務', items:['ComboLLM (LLMaaS)','ComboRAG+','ComboAI Pro / Pro Max','WrenAI 商業智能'], bg:'#f9f0ff', border:'#d3adf7'},
          {icon:'☁️', title:'雲端基礎設施', items:['ComboStack VM','GPU 高效能運算','VDI 虛擬桌面','Kubernetes (K8s)'], bg:'#e6f7ff', border:'#91d5ff'},
          {icon:'🏢', title:'企業應用', items:['Odoo ERP 系統','ComboStor 儲存服務','FoodPro 產業應用','Apache + MySQL'], bg:'#fff7e6', border:'#ffd591'},
          {icon:'🏭', title:'數據中心管理', items:['DCIM 基礎設施管理','EEMS 能源管理','WorkAI 職場 AI'], bg:'#e6fffb', border:'#87e8de'},
          {icon:'🔧', title:'自訂服務', items:['Docker Compose 上傳','自動表單生成','一鍵部署任何應用'], bg:'#f6ffed', border:'#b7eb8f'},
          {icon:'🛡️', title:'運維管理', items:['Chat/Ops 運維聊天','Restic 備份系統','Prometheus 監控'], bg:'#fff1f0', border:'#ffa39e'},
        ].map((c,i)=>(
          <div key={i} style={{ background:c.bg, borderRadius:8, padding:12, border:`1px solid ${c.border}` }}>
            <div style={{ fontSize:20, marginBottom:4 }}>{c.icon}</div>
            <div style={{ fontWeight:700, fontSize:13, color:'#262626', marginBottom:6 }}>{c.title}</div>
            {c.items.map((t,j)=>(<div key={j} style={{ fontSize:11, color:'#595959', padding:'1px 0' }}>• {t}</div>))}
          </div>
        ))}
      </div>
    ),
  },
  {
    title: 'DCIM — AI AUTOMATION ESSENTIAL',
    subtitle: 'DCIM — AI 自動化服務的必備功能',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, marginBottom:16 }}>
          {[
            {icon:'📋',t:'設備資產管理'},{icon:'🏢',t:'機房配置管理'},{icon:'🔗',t:'架構關聯映射'},{icon:'📊',t:'容量規劃'},
            {icon:'🤖',t:'AI 整合基礎'},{icon:'🔄',t:'生命週期管理'},{icon:'🌐',t:'Web 整合'},
          ].map((c,i)=>(
            <div key={i} style={{ textAlign:'center', background:'#e6fffb', borderRadius:6, padding:'8px 4px', border:'1px solid #87e8de' }}>
              <div style={{ fontSize:18 }}>{c.icon}</div>
              <div style={{ fontSize:10, fontWeight:600, color:'#006d75', marginTop:2 }}>{c.t}</div>
            </div>
          ))}
        </div>
        <div style={{ background:'#fafafa', borderRadius:8, padding:12, overflow:'auto' }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>競爭對比 — DCIM 能力</div>
          <table style={{ width:'100%', fontSize:11, borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f0f5ff' }}>
              {['平台','VM','GPU','設備','機房','AI整合'].map((h,i)=>(<th key={i} style={{ padding:'5px 6px', textAlign:'center', fontWeight:600, borderBottom:'2px solid #d6e4ff' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {[
                ['VMware','✓','△','✗','✗','✗'],
                ['Nutanix','✓','△','✗','✗','✗'],
                ['GPU Mgr','✗','✓','✗','✗','✗'],
                ['AWS/Azure','✓','✓','✗','✗','△'],
                ['Comboware','✓','✓','✓','✓','✓'],
              ].map((r,i)=>(
                <tr key={i} style={{ borderBottom:'1px solid #f0f0f0', background: i===4?'#f6ffed':'transparent', fontWeight: i===4?700:400 }}>
                  {r.map((c,j)=>(<td key={j} style={{ padding:'4px 6px', textAlign:'center', color: c==='✓'?'#52c41a':c==='✗'?'#ff4d4f':'#faad14' }}>{c}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    title: 'SECURITY & MONITORING',
    subtitle: '多層安全防護 + 全方位即時監控',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:'#003a8c', marginBottom:10 }}>🛡️ 六層安全防護</div>
            {[
              {t:'認證授權',d:'Session · 30min 逾時 · LDAP Ready'},
              {t:'權限控制',d:'RBAC 5級 · 公司隔離'},
              {t:'資料安全',d:'Prepared Statements · 密碼雜湊'},
              {t:'API 防護',d:'CSRF Token · Sanitization'},
              {t:'主機安全',d:'SSH 金鑰 · 密碼零儲存'},
              {t:'傳輸安全',d:'SSL/TLS · HTTPS 代理'},
            ].map((c,i)=>(
              <div key={i} style={{ display:'flex', gap:8, padding:'4px 0', fontSize:12 }}>
                <div style={{ fontWeight:600, color:'#262626', minWidth:70 }}>{c.t}</div>
                <div style={{ color:'#8c8c8c' }}>{c.d}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:'#003a8c', marginBottom:10 }}>📊 Prometheus 智能監控</div>
            {[
              'Node Exporter (CPU/Mem/Disk/Net)',
              'GPU Exporter (利用率/記憶體/溫度)',
              '零停機服務發現 — File-based SD',
              'Material Design 視覺化儀表板',
              '7+ 監控 API — 概覽/圖表/伺服器',
            ].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'3px 0' }}>▸ {t}</div>
            ))}
          </div>
        </div>
        <div style={{ background:'linear-gradient(90deg,#f9f0ff,#e6f7ff)', borderRadius:8, padding:10, textAlign:'center' }}>
          <div style={{ fontWeight:700, fontSize:13, color:'#003a8c' }}>
            縱深防禦 (Defense in Depth) — 核心層 + 邊緣層協調作戰，消除單點故障
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'MOBILE APP & i18n',
    subtitle: 'Mobile App 即時管理 + 全球 12 語言支援',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div style={{ background:'#f6ffed', borderRadius:10, padding:14, border:'1px solid #b7eb8f' }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>🤖 Android App</div>
            {['即時監控儀表板','推播告警通知','服務狀態管理','資源使用查看','快速部署操作'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>• {t}</div>
            ))}
          </div>
          <div style={{ background:'#f0f5ff', borderRadius:10, padding:14, border:'1px solid #d6e4ff' }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>🍎 iOS App</div>
            {['Face ID 安全登入','Widget 快速瀏覽','Siri 語音指令','Apple Watch 告警','離線資料快取'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>• {t}</div>
            ))}
          </div>
        </div>
        <div style={{ background:'#fafafa', borderRadius:8, padding:12 }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>🌍 全球 12 語言支援</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {['🇬🇧 English','🇹🇼 繁體中文','🇯🇵 日本語','🇰🇷 한국어','🇹🇭 ไทย','🇻🇳 Tiếng Việt','🇫🇷 Français','🇩🇪 Deutsch','🇪🇸 Español','🇮🇹 Italiano','🇳🇱 Nederlands','🇵🇹 Português'].map((l,i)=>(
              <span key={i} style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:4, padding:'3px 8px', fontSize:11 }}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'BUSINESS MODEL',
    subtitle: '全球雙軌商業模式 + 資源共享市場',
    content: (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div style={{ background:'#e6f7ff', borderRadius:10, padding:14, border:'1px solid #91d5ff' }}>
            <div style={{ fontWeight:700, fontSize:14, color:'#0050b3', marginBottom:8 }}>🏢 B2B — 企業服務</div>
            {['企業私有雲：AI/雲服務平台','IDC 轉型：傳統 IDC → 智能雲','系統整合：快速交付客製化方案','DC 營運：DCIM + EEMS 全面管理'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>▸ {t}</div>
            ))}
          </div>
          <div style={{ background:'#fff7e6', borderRadius:10, padding:14, border:'1px solid #ffd591' }}>
            <div style={{ fontWeight:700, fontSize:14, color:'#ad6800', marginBottom:8 }}>👤 B2C — 用戶 & 全球市場</div>
            {['中小企業 SaaS：ERP/AI 助手','開發者平台：GPU 租用、AI 訓練','教育訓練：VDI 遠距教學','全球共享：任何人提供/使用資源'].map((t,i)=>(
              <div key={i} style={{ fontSize:12, color:'#595959', padding:'2px 0' }}>▸ {t}</div>
            ))}
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
          {[
            {n:'50%+',l:'成本優勢 vs 公有雲', bg:'#f6ffed'},
            {n:'20+',l:'服務類型一站管理', bg:'#e6f7ff'},
            {n:'10+',l:'生態角色全面覆蓋', bg:'#fff7e6'},
            {n:'Global',l:'全球資源共享', bg:'#f9f0ff'},
          ].map((s,i)=>(
            <div key={i} style={{ textAlign:'center', background:s.bg, borderRadius:8, padding:'10px 6px' }}>
              <div style={{ fontSize:18, fontWeight:800, color:'#003a8c' }}>{s.n}</div>
              <div style={{ fontSize:10, color:'#595959', marginTop:2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
];

// Highlight cards for the inline showcase
const HIGHLIGHT_CARDS = [
  { icon: '🤖', title: 'AI 一站式平台', desc: '20+ 服務類型，從 LLM 到 DCIM 全面覆蓋', gradient: 'linear-gradient(135deg, #667eea, #764ba2)' },
  { icon: '🌐', title: '全球資源共享', desc: 'CP 值完勝三大公有雲，成本節省 50%+', gradient: 'linear-gradient(135deg, #f093fb, #f5576c)' },
  { icon: '⚡', title: '零接觸自動部署', desc: 'Ansible + Docker，70% 部署時間節省', gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)' },
  { icon: '🛡️', title: '30 年企業 IT 經驗', desc: '全球 IDC 佈局 + 完整資訊委外服務', gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)' },
];

// ---------------------------------------------------------------------------
// SlideViewer Component (with analytics tracking + lead capture)
// ---------------------------------------------------------------------------
function SlideViewer({ visible, onClose, token }) {
  const [current, setCurrent] = useState(0);
  const [viewedSlides, setViewedSlides] = useState(new Set());
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadForm, setLeadForm] = useState({
    company_name: '', contact_person: '', email: '', phone: '', interest: '',
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  // Track slide view
  useEffect(() => {
    if (!visible) return;
    if (!viewedSlides.has(current)) {
      setViewedSlides(prev => new Set([...prev, current]));
      analyticsAPI.trackVisit({
        page: 'slide_viewer', token: token || null, slide_index: current,
      }).catch(() => {});
    }
    // Show lead capture after viewing 4 unique slides (if not already submitted)
    if (viewedSlides.size >= 3 && !leadSubmitted && !showLeadForm) {
      const timer = setTimeout(() => setShowLeadForm(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [current, visible, viewedSlides, leadSubmitted, showLeadForm, token]);

  // Track slide viewer open
  useEffect(() => {
    if (visible) {
      analyticsAPI.trackVisit({
        page: 'slide_viewer_open', token: token || null,
      }).catch(() => {});
      setCurrent(0);
      setViewedSlides(new Set());
    }
  }, [visible, token]);

  const handleLeadSubmit = async () => {
    if (!leadForm.contact_person || !leadForm.email) {
      message.warning('請填寫聯絡人與 Email');
      return;
    }
    setLeadSubmitting(true);
    try {
      await analyticsAPI.captureLead({
        ...leadForm, token: token || null,
        source_page: 'slide_viewer', slide_index: current,
      });
      setLeadSubmitted(true);
      setShowLeadForm(false);
      message.success('感謝您的留言！我們將盡快與您聯繫');
    } catch {
      message.error('提交失敗，請稍後再試');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (!visible) return;
    if (e.key === 'ArrowRight' || e.key === ' ') setCurrent(c => Math.min(c + 1, SLIDES.length - 1));
    if (e.key === 'ArrowLeft') setCurrent(c => Math.max(c - 1, 0));
    if (e.key === 'Escape') onClose();
  }, [visible, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 3000,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 20px', background: 'rgba(0,0,0,0.4)',
      }}>
        <div style={{ color: '#91d5ff', fontSize: 14, fontWeight: 600 }}>
          COMBOWARE AI Intelligent Infrastructure Platform
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
            {current + 1} / {SLIDES.length}
          </span>
          <div
            onClick={onClose}
            style={{ color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1, fontWeight: 300 }}
          >×</div>
        </div>
      </div>

      {/* Slide content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 60px', position: 'relative' }}>
        {/* Left arrow */}
        <div
          onClick={() => setCurrent(c => Math.max(c - 1, 0))}
          style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            width: 44, height: 44, borderRadius: '50%', background: current > 0 ? 'rgba(255,255,255,0.15)' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: current > 0 ? 'pointer' : 'default', color: current > 0 ? '#fff' : 'transparent',
            fontSize: 24, transition: 'all 0.2s',
          }}
        >‹</div>

        {/* Slide card */}
        <div style={{
          background: '#fff', borderRadius: 16, maxWidth: 780, width: '100%',
          maxHeight: 'calc(100vh - 120px)', overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}>
          {/* Slide header */}
          <div style={{
            background: 'linear-gradient(135deg, #001529, #003a8c, #0050b3)',
            padding: '24px 28px', borderRadius: '16px 16px 0 0',
          }}>
            <div style={{ color: '#91d5ff', fontSize: 12, fontWeight: 600, letterSpacing: 3, marginBottom: 6 }}>
              {SLIDES[current].title}
            </div>
            <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>
              {SLIDES[current].subtitle}
            </div>
          </div>
          {/* Slide body */}
          <div style={{ padding: '20px 28px 28px' }}>
            {SLIDES[current].content}
          </div>
        </div>

        {/* Right arrow */}
        <div
          onClick={() => setCurrent(c => Math.min(c + 1, SLIDES.length - 1))}
          style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            width: 44, height: 44, borderRadius: '50%', background: current < SLIDES.length - 1 ? 'rgba(255,255,255,0.15)' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: current < SLIDES.length - 1 ? 'pointer' : 'default', color: current < SLIDES.length - 1 ? '#fff' : 'transparent',
            fontSize: 24, transition: 'all 0.2s',
          }}
        >›</div>
      </div>

      {/* Bottom slide indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '12px 0 16px' }}>
        {SLIDES.map((_, i) => (
          <div
            key={i}
            onClick={() => setCurrent(i)}
            style={{
              width: i === current ? 24 : 8, height: 8, borderRadius: 4,
              background: i === current ? '#1890ff' : 'rgba(255,255,255,0.3)',
              cursor: 'pointer', transition: 'all 0.3s',
            }}
          />
        ))}
      </div>

      {/* Lead Capture Popup */}
      {showLeadForm && !leadSubmitted && (
        <div style={{
          position: 'absolute', bottom: 60, right: 24, width: 340,
          background: '#fff', borderRadius: 12, padding: 20,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 3100,
          animation: 'slideUp 0.4s ease',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#003a8c' }}>
              想了解更多？
            </div>
            <div onClick={() => setShowLeadForm(false)}
              style={{ cursor: 'pointer', color: '#999', fontSize: 18 }}>×</div>
          </div>
          <div style={{ fontSize: 13, color: '#595959', marginBottom: 14 }}>
            留下聯絡方式，我們的專業團隊將為您量身規劃 AI 基礎設施方案
          </div>
          {[
            { key: 'company_name', placeholder: '公司名稱' },
            { key: 'contact_person', placeholder: '聯絡人姓名 *' },
            { key: 'email', placeholder: 'Email *' },
            { key: 'phone', placeholder: '聯絡電話' },
          ].map(f => (
            <input
              key={f.key}
              placeholder={f.placeholder}
              value={leadForm[f.key]}
              onChange={e => setLeadForm(prev => ({ ...prev, [f.key]: e.target.value }))}
              style={{
                width: '100%', padding: '8px 12px', marginBottom: 8, borderRadius: 6,
                border: '1px solid #d9d9d9', fontSize: 13, outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          ))}
          <select
            value={leadForm.interest}
            onChange={e => setLeadForm(prev => ({ ...prev, interest: e.target.value }))}
            style={{
              width: '100%', padding: '8px 12px', marginBottom: 12, borderRadius: 6,
              border: '1px solid #d9d9d9', fontSize: 13, color: leadForm.interest ? '#262626' : '#bfbfbf',
              background: '#fff', boxSizing: 'border-box',
            }}
          >
            <option value="">選擇感興趣的方案...</option>
            <option value="AI 推論/訓練平台">AI 推論/訓練平台</option>
            <option value="私有雲部署">私有雲部署</option>
            <option value="HCI 超融合架構">HCI 超融合架構</option>
            <option value="DCIM 機房管理">DCIM 機房管理</option>
            <option value="全球資源共享平台">全球資源共享平台</option>
            <option value="其他諮詢">其他諮詢</option>
          </select>
          <button
            onClick={handleLeadSubmit}
            disabled={leadSubmitting}
            style={{
              width: '100%', padding: '10px 0', borderRadius: 6, border: 'none',
              background: 'linear-gradient(135deg, #1890ff, #0050b3)', color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: leadSubmitting ? 'wait' : 'pointer',
              opacity: leadSubmitting ? 0.7 : 1,
            }}
          >
            {leadSubmitting ? '提交中...' : '立即諮詢'}
          </button>
          <div style={{ fontSize: 11, color: '#bfbfbf', textAlign: 'center', marginTop: 8 }}>
            您的資料將受到嚴格保密
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function FullSurveyPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Form fields
  const [regionType, setRegionType] = useState('');
  const [regionAreas, setRegionAreas] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [representative, setRepresentative] = useState('');
  const [address, setAddress] = useState('');
  const [fax, setFax] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactMobile, setContactMobile] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Seminar registration state
  const [seminarRegistered, setSeminarRegistered] = useState(false);
  const [showSeminarWidget, setShowSeminarWidget] = useState(false);
  const [showSeminarModal, setShowSeminarModal] = useState(false);
  const [, setSeminarAutoPopShown] = useState(false);
  const [seminarForm, setSeminarForm] = useState({ contact_person: '', job_title: '', phone: '' });
  const [seminarSubmitting, setSeminarSubmitting] = useState(false);
  const [seminarSuccess, setSeminarSuccess] = useState(false);

  // Slide viewer state
  const [showSlideViewer, setShowSlideViewer] = useState(false);

  useEffect(() => {
    if (!token) { setError('invalid'); setLoading(false); return; }
    tenderAPI.getFullSurvey(token)
      .then((res) => {
        const data = res.data || res;
        setSurveyData(data);
        // Pre-fill if dealer info exists
        if (data.dealer) {
          setCompanyName(data.dealer.company || '');
          setContactPerson(data.dealer.name || '');
        }
        // Track survey page visit
        analyticsAPI.trackVisit({ page: 'survey', token }).catch(() => {});
      })
      .catch(() => setError('not_found'))
      .finally(() => setLoading(false));
  }, [token]);

  // Seminar status check + timers
  useEffect(() => {
    if (!surveyData || submitted) return;
    tenderAPI.checkSeminarStatus(token).then(res => {
      const data = res.data || res;
      if (data.registered) {
        setSeminarRegistered(true);
      } else {
        // Pre-fill form
        setSeminarForm(prev => ({
          ...prev,
          contact_person: surveyData.dealer?.name || '',
        }));
        // 3s: show widget
        const t1 = setTimeout(() => setShowSeminarWidget(true), 3000);
        // 5s: auto-pop modal (once)
        const t2 = setTimeout(() => {
          setShowSeminarModal(true);
          setSeminarAutoPopShown(true);
        }, 5000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
      }
    }).catch(() => {});
  }, [surveyData, submitted, token]);

  const handleRegionToggle = (area) => {
    setRegionAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
  };

  const handleBrandToggle = (brandId) => {
    setSelectedBrands((prev) =>
      prev.includes(brandId) ? prev.filter((id) => id !== brandId) : [...prev, brandId]
    );
  };

  const handleSubmit = async () => {
    if (!contactEmail) { alert('請填寫聯絡 E-mail'); return; }
    if (!companyName) { alert('請填寫經銷商寶號'); return; }
    if (selectedBrands.length === 0) { alert('請至少選擇一個品牌'); return; }

    setSubmitting(true);
    try {
      await tenderAPI.submitFullSurvey(token, {
        region_type: regionType,
        region_areas: regionAreas,
        interested_brand_ids: selectedBrands,
        company_name: companyName,
        representative,
        address,
        fax,
        contact_person: contactPerson,
        contact_phone: contactPhone,
        contact_mobile: contactMobile,
        contact_email: contactEmail,
        notes,
      });
      setSubmitted(true);
      if (!seminarRegistered) {
        setTimeout(() => setShowSeminarModal(true), 500);
      }
    } catch (err) {
      alert('送出失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSeminarRegister = async () => {
    if (!seminarForm.job_title.trim() || !seminarForm.phone.trim()) {
      message.warning('請填寫職稱和電話');
      return;
    }
    setSeminarSubmitting(true);
    try {
      await tenderAPI.registerSeminar(token, seminarForm);
      setSeminarSuccess(true);
      setTimeout(() => {
        setShowSeminarModal(false);
        setSeminarRegistered(true);
        setShowSeminarWidget(false);
      }, 3000);
    } catch (err) {
      message.error('報名失敗: ' + (err.message || '未知錯誤'));
    } finally {
      setSeminarSubmitting(false);
    }
  };

  if (loading) return <div style={S.page}><div style={S.center}>載入中...</div></div>;
  if (error) return (
    <div style={S.page}>
      <div style={S.center}>
        <h2 style={{ color: '#c62828' }}>連結無效或已過期</h2>
        <p style={{ color: '#666' }}>請洽 周伶瑋 (Jolie) 02-8773-0263 #181</p>
      </div>
    </div>
  );
  if (submitted) return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.headerTitle}>宇聯科技</h1>
        <p style={S.headerSub}>ULINK TECHNOLOGY</p>
      </div>
      <div style={S.center}>
        <h2 style={{ color: '#2e7d32' }}>意向書已成功提交！</h2>
        <p>感謝您的回覆，我們將盡快與您聯繫。</p>
        <p>業務人員將根據意向書內容提供「價格承諾書」。</p>
        <p style={{ color: '#888', marginTop: 20 }}>
          如有任何疑問，請洽 周伶瑋 (Jolie) 02-8773-0263 #181
        </p>
      </div>
    </div>
  );

  const campaign = surveyData?.campaign || {};
  const brands = surveyData?.brands || [];

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <h1 style={S.headerTitle}>宇聯科技</h1>
        <p style={S.headerSub}>ULINK TECHNOLOGY</p>
        <div style={S.badge}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{campaign.case_number}</div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{campaign.title}</div>
        </div>
      </div>

      <div style={S.body}>
        {/* ============================================================= */}
        {/* Comboware AI Infra Product Showcase (addon-2)                  */}
        {/* ============================================================= */}
        <div style={{
          background: 'linear-gradient(135deg, #001529 0%, #003a8c 50%, #0050b3 100%)',
          borderRadius: 12,
          padding: '28px 24px',
          marginBottom: 24,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative circles */}
          <div style={{ position:'absolute', top:-40, right:-40, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,0.05)' }} />
          <div style={{ position:'absolute', bottom:-30, left:-30, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.03)' }} />

          <div style={{ position:'relative', zIndex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <span style={{ fontSize:11, background:'rgba(250,173,20,0.25)', color:'#fadb14', padding:'2px 10px', borderRadius:10, fontWeight:600, letterSpacing:1 }}>FEATURED</span>
            </div>
            <h2 style={{ margin:'0 0 6px', fontSize:22, fontWeight:800, letterSpacing:2 }}>
              COMBOWARE AI Intelligent Infrastructure
            </h2>
            <p style={{ margin:'0 0 20px', fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.6 }}>
              全球唯一整合 AI + Cloud + DCIM + 資源共享的一站式平台 — 掌握經銷 Comboware 的超級優勢
            </p>

            {/* 4 highlight cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, marginBottom:20 }}>
              {HIGHLIGHT_CARDS.map((card, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 10,
                  padding: '16px 12px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  textAlign: 'center',
                  transition: 'transform 0.2s, background 0.2s',
                  cursor: 'pointer',
                }} onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.18)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                   onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.transform='translateY(0)'; }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{card.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{card.title}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>{card.desc}</div>
                </div>
              ))}
            </div>

            {/* Key stats row */}
            <div style={{ display:'flex', justifyContent:'center', gap:24, marginBottom:20, flexWrap:'wrap' }}>
              {[
                {n:'20+', l:'服務類型'}, {n:'10+', l:'生態角色'}, {n:'Global', l:'資源共享'},
                {n:'60+', l:'API Endpoints'}, {n:'Mobile', l:'iOS & Android'},
              ].map((s,i) => (
                <div key={i} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:20, fontWeight:800, color:'#fadb14' }}>{s.n}</div>
                  <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)' }}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* CTA button */}
            <div style={{ textAlign:'center' }}>
              <button
                onClick={() => setShowSlideViewer(true)}
                style={{
                  padding: '12px 36px',
                  background: 'linear-gradient(135deg, #faad14, #fa8c16)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(250,173,20,0.4)',
                  letterSpacing: 1,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='scale(1.03)'; e.currentTarget.style.boxShadow='0 6px 24px rgba(250,173,20,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(250,173,20,0.4)'; }}
              >
                瀏覽完整產品簡報 ({SLIDES.length} 頁)
              </button>
            </div>
          </div>
        </div>

        {/* Section 1: 品牌介紹 & 選擇 */}
        <div style={S.section}>
          <h3 style={S.sectionTitle}>投標跟進指定品牌 *</h3>
          <p style={{ color: '#666', marginBottom: 16 }}>
            請勾選您有興趣投標的品牌（可複選）
          </p>
          {brands.map((brand) => (
            <div key={brand.id} style={S.brandCard}>
              <label style={S.brandLabel}>
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(brand.id)}
                  onChange={() => handleBrandToggle(brand.id)}
                  style={S.checkbox}
                />
                <div>
                  <strong>{brand.name}</strong>
                  {brand.group_number && <span style={S.tag}>第{brand.group_number}組</span>}
                  {brand.item_range_start && (
                    <span style={S.tag}>項次 {brand.item_range_start}-{brand.item_range_end}</span>
                  )}
                  {brand.description && <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{brand.description}</p>}
                </div>
              </label>
              {brand.group_number && (
                <div style={{ marginLeft: 30, marginTop: 6, fontSize: 13, color: '#555' }}>
                  {brand.name} 第{brand.group_number}組{brand.item_range_start ? ` 項次 ${brand.item_range_start}-${brand.item_range_end}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Section 2: 經銷商類型 */}
        <div style={S.section}>
          <h3 style={S.sectionTitle}>全區/分區 經銷商 *</h3>
          <label style={S.radioLabel}>
            <input type="radio" name="regionType" value="全區經銷商"
              checked={regionType === '全區經銷商'} onChange={(e) => setRegionType(e.target.value)} />
            <span>全區經銷商</span>
          </label>
          <label style={S.radioLabel}>
            <input type="radio" name="regionType" value="分區經銷商"
              checked={regionType === '分區經銷商'} onChange={(e) => setRegionType(e.target.value)} />
            <span>分區經銷商</span>
          </label>

          {regionType === '分區經銷商' && (
            <div style={S.regionGrid}>
              <p style={{ gridColumn: '1/-1', color: '#666', fontSize: 13 }}>請選擇經銷區域（最多5個）：</p>
              {TAIWAN_REGIONS.map((area) => (
                <label key={area} style={S.regionItem}>
                  <input
                    type="checkbox"
                    checked={regionAreas.includes(area)}
                    onChange={() => handleRegionToggle(area)}
                    disabled={!regionAreas.includes(area) && regionAreas.length >= 5}
                    style={S.checkbox}
                  />
                  <span>{area}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Section 3: 經銷商資料 */}
        <div style={S.section}>
          <h3 style={S.sectionTitle}>經銷商基本資料</h3>
          <div style={S.formGrid}>
            <div style={S.field}>
              <label style={S.label}>經銷商寶號 *</label>
              <input style={S.input} value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="公司全名" />
            </div>
            <div style={S.field}>
              <label style={S.label}>經銷商代表人 *</label>
              <input style={S.input} value={representative} onChange={(e) => setRepresentative(e.target.value)} />
            </div>
            <div style={S.fieldFull}>
              <label style={S.label}>公司地址 *</label>
              <input style={S.input} value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>公司傳真</label>
              <input style={S.input} value={fax} onChange={(e) => setFax(e.target.value)} placeholder="02-321654988" />
            </div>
            <div style={S.field}>
              <label style={S.label}>經銷商聯絡人 *</label>
              <input style={S.input} value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>聯絡人電話/分機 *</label>
              <input style={S.input} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="02-321654987 #123" />
            </div>
            <div style={S.field}>
              <label style={S.label}>聯絡人行動電話 *</label>
              <input style={S.input} value={contactMobile} onChange={(e) => setContactMobile(e.target.value)} placeholder="0999-321654" />
            </div>
            <div style={S.fieldFull}>
              <label style={S.label}>聯絡 E-mail * <span style={{ color: '#888', fontWeight: 'normal' }}>（價格承諾書將寄至此信箱）</span></label>
              <input style={S.input} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Section 4: 備註 */}
        <div style={S.section}>
          <h3 style={S.sectionTitle}>備註</h3>
          <textarea
            style={S.textarea}
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="如有任何問題或建議，歡迎在此留言..."
          />
        </div>

        {/* Submit */}
        <div style={{ textAlign: 'center', margin: '30px 0' }}>
          <button onClick={handleSubmit} disabled={submitting} style={S.submitBtn}>
            {submitting ? '送出中...' : '送出意向書'}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div style={S.footer}>
        <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>聯絡窗口</p>
        <p>周伶瑋 (Jolie) 02-8773-0263 #181 / jolie.chou@ulink.com.tw</p>
        <p>蘇鈺涵 (Anny) 02-8773-0263 #182 / anny.su@ulink.com.tw</p>
        <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.5)', letterSpacing: 2 }}>宇聯科技 敬上</p>
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: translateY(-50%) scale(1); }
          50% { transform: translateY(-50%) scale(1.05); }
        }
      `}</style>

      {/* Seminar floating widget */}
      {showSeminarWidget && !seminarRegistered && (
        <div
          onClick={() => setShowSeminarModal(true)}
          style={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'linear-gradient(135deg, #001529, #0050b3)',
            color: '#fff',
            padding: '16px 14px',
            borderRadius: '12px 0 0 12px',
            cursor: 'pointer',
            zIndex: 1000,
            boxShadow: '-4px 0 20px rgba(0,0,0,0.2)',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 2,
            animation: 'pulse 2s infinite',
            border: '2px solid #faad14',
            borderRight: 'none',
          }}
        >
          標案策略論壇 · 立即報名
        </div>
      )}

      {/* Seminar registration modal */}
      {showSeminarModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, maxWidth: 480, width: '90%',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
          }}>
            {/* Close button */}
            <div
              onClick={() => setShowSeminarModal(false)}
              style={{
                position: 'absolute', top: 8, right: 8, fontSize: 28,
                cursor: 'pointer', color: '#fff', zIndex: 10, fontWeight: 'bold',
                width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.3)', borderRadius: '50%',
              }}
            >×</div>

            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #001529, #003a8c, #0050b3)',
              padding: '28px 24px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🤖</div>
              <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 700 }}>
                1150201 標案策略論壇
              </h2>
              <p style={{ color: '#91d5ff', margin: '8px 0 0', fontSize: 14 }}>
                掌握 AI 驅動的 Comboware 套裝軟體獲利戰略
              </p>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              {seminarSuccess ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                  <h3 style={{ color: '#52c41a', fontSize: 20, margin: '0 0 12px' }}>報名成功！</h3>
                  <p style={{ color: '#666', fontSize: 14 }}>
                    我們將於論壇前發送詳細通知至您的 E-mail
                  </p>
                </div>
              ) : (
                <>
                  <div style={{
                    background: '#fff7e6', border: '1px solid #ffe58f', borderRadius: 8,
                    padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#ad6800',
                  }}>
                    AI 講師親自解密：Comboware 套裝軟體標案策略，名額有限，立即報名！
                  </div>

                  {/* Company Name - readonly */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>公司名稱</label>
                    <input
                      value={surveyData?.dealer?.company || ''}
                      readOnly
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #d9d9d9', background: '#f5f5f5', fontSize: 14,
                        boxSizing: 'border-box', color: '#595959',
                      }}
                    />
                  </div>

                  {/* Contact Person - editable */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>聯絡人</label>
                    <input
                      value={seminarForm.contact_person}
                      onChange={e => setSeminarForm(prev => ({ ...prev, contact_person: e.target.value }))}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #d9d9d9', fontSize: 14, boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Email - readonly */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>聯絡 E-mail</label>
                    <input
                      value={surveyData?.dealer?.email || surveyData?.dealer?.name || ''}
                      readOnly
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #d9d9d9', background: '#f5f5f5', fontSize: 14,
                        boxSizing: 'border-box', color: '#595959',
                      }}
                    />
                  </div>

                  {/* Job Title - required */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                      單位職稱 <span style={{ color: '#ff4d4f' }}>*</span>
                    </label>
                    <input
                      value={seminarForm.job_title}
                      onChange={e => setSeminarForm(prev => ({ ...prev, job_title: e.target.value }))}
                      placeholder="例：業務經理"
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #d9d9d9', fontSize: 14, boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Phone - required */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                      聯絡電話 <span style={{ color: '#ff4d4f' }}>*</span>
                    </label>
                    <input
                      value={seminarForm.phone}
                      onChange={e => setSeminarForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="例：02-1234-5678"
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #d9d9d9', fontSize: 14, boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSeminarRegister}
                    disabled={seminarSubmitting}
                    style={{
                      width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                      background: 'linear-gradient(135deg, #faad14, #fa8c16)',
                      color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(250,173,20,0.4)',
                      opacity: seminarSubmitting ? 0.7 : 1,
                    }}
                  >
                    {seminarSubmitting ? '報名中...' : '立即報名'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Slide Viewer Modal */}
      <SlideViewer visible={showSlideViewer} onClose={() => setShowSlideViewer(false)} token={token} />
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: '#f0f2f5',
    fontFamily: "'Microsoft JhengHei', 'Noto Sans TC', Arial, sans-serif",
  },
  header: {
    background: 'linear-gradient(135deg, #001529, #003a70, #1890ff)',
    padding: '40px 24px 36px',
    textAlign: 'center',
    color: '#fff',
  },
  headerTitle: { fontSize: 32, fontWeight: 700, margin: 0, letterSpacing: 6 },
  headerSub: { fontSize: 14, color: 'rgba(255,255,255,0.75)', marginTop: 6, letterSpacing: 2 },
  badge: {
    display: 'inline-block',
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: 8,
    padding: '10px 28px',
    marginTop: 20,
  },
  body: { maxWidth: 800, margin: '0 auto', padding: '24px 16px 48px' },
  section: {
    background: '#fff',
    borderRadius: 8,
    padding: '24px',
    marginBottom: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  sectionTitle: { fontSize: 18, fontWeight: 600, color: '#001529', marginBottom: 16, marginTop: 0 },
  brandCard: {
    background: '#f9f9f9',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 10,
    border: '1px solid #e8e8e8',
  },
  brandLabel: { display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 15 },
  tag: {
    display: 'inline-block',
    background: '#e6f7ff',
    color: '#1890ff',
    padding: '1px 8px',
    borderRadius: 4,
    fontSize: 12,
    marginLeft: 8,
  },
radioLabel: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 15, cursor: 'pointer' },
  regionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    marginTop: 12,
    padding: 16,
    background: '#f9f9f9',
    borderRadius: 6,
  },
  regionItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' },
  checkbox: { width: 18, height: 18, cursor: 'pointer' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: {},
  fieldFull: { gridColumn: '1 / -1' },
  label: { display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6, color: '#333' },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  submitBtn: {
    padding: '14px 60px',
    background: 'linear-gradient(135deg, #001529, #1890ff)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 18,
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(24,144,255,0.4)',
  },
  footer: {
    background: '#001529',
    color: 'rgba(255,255,255,0.85)',
    padding: '36px 24px',
    textAlign: 'center',
    fontSize: 14,
    lineHeight: 1.8,
  },
  center: { maxWidth: 600, margin: '80px auto', padding: '0 16px', textAlign: 'center' },
};
